import { useEffect, useMemo, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { ShieldCheck, ShieldAlert, Wrench, CheckCircle2, AlertTriangle, Loader2, Download, Search, Phone, Building2, Globe, MessageCircle, Users, BellOff, Undo2, Archive, ArchiveRestore, Trash2, X as ClearIcon } from "lucide-react";
import { toast } from "sonner";
import { V } from "@/lib/vocab";

const sourceLabelFactory = () => ({ web_opt_in_page: "Web opt-in page", inbound_text: "First text-in", keyword: "Text-in keyword", roster: `Roster (${V.one("org_short")})` });
const SOURCE_ICON = { web_opt_in_page: Globe, inbound_text: MessageCircle, keyword: MessageCircle, roster: Users };

const sourceGroupsFactory = () => [
  { key: "web_opt_in_page", label: "Web / QR", icon: Globe, match: (s) => s === "web_opt_in_page" },
  { key: "inbound_text", label: "Text-in", icon: MessageCircle, match: (s) => s === "inbound_text" || s === "keyword" },
  { key: "roster", label: `Roster (${V.one("org_short")})`, icon: Users, match: (s) => s === "roster" },
];

export default function OptIns() {
  const SOURCE_LABEL = sourceLabelFactory();
  const SOURCE_GROUPS = sourceGroupsFactory();
  const { tenantId, tenants } = useDash();
  const [optins, setOptins] = useCachedState("admin-optins", null);
  const [optouts, setOptouts] = useCachedState("admin-optouts", []);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(tenantId || "all");
  // iter195r — bulk-select + archive + delete controls. Managers can now clean up
  // finished-work opt-ins without losing the TCPA audit trail (archive) or hard-delete
  // duplicates/test records (delete). Toggling "Show archived" reveals hidden ones.
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadOptouts = () => api.get("/admin/optouts").then((r) => setOptouts(r.data.optouts || [])).catch(() => {});
  const loadOptins = () => api.get(`/admin/optins${showArchived ? "?include_archived=true" : ""}`).then((r) => setOptins(r.data.optins || []));
  useEffect(() => { loadOptins(); loadOptouts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showArchived]);
  useEffect(() => { if (tenantId) setScope(tenantId); }, [tenantId]);
  // Selection resets when the visible list changes (scope/query/archived toggle) —
  // avoids acting on rows the user can no longer see.
  useEffect(() => { setSelected(new Set()); }, [scope, q, showArchived]);

  // Scope the raw records to the selected community before anything else derives from them.
  const scoped = useMemo(() => {
    const list = optins || [];
    return scope === "all" ? list : list.filter((o) => o.tenant_id === scope);
  }, [optins, scope]);

  const analytics = useMemo(() => {
    const list = scoped;
    const bySource = SOURCE_GROUPS.map((g) => ({ ...g, count: list.filter((o) => g.match(o.source)).length }));
    const commMap = {};
    for (const o of list) {
      const key = o.community || `— No ${V.lc("org")} —`;
      if (!commMap[key]) commMap[key] = { community: key, total: 0, web_opt_in_page: 0, inbound_text: 0, roster: 0 };
      commMap[key].total += 1;
      const g = SOURCE_GROUPS.find((x) => x.match(o.source));
      if (g) commMap[key][g.key] += 1;
    }
    const byCommunity = Object.values(commMap).sort((a, b) => b.total - a.total);
    return { bySource, byCommunity };
  }, [scoped]);

  if (!optins) return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;

  const filtered = scoped.filter((o) => {
    if (!q) return true;
    const s = `${o.name} ${o.phone} ${o.community}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const headers = ["Date", "Name", "Phone", "Community", "Source", "Consent", "Consent Text", "IP"];
    const rows = scoped.map((o) => [
      new Date(o.created_at).toISOString(),
      o.name || "",
      o.phone || "",
      o.community || "",
      SOURCE_LABEL[o.source] || o.source || "",
      o.consent ? "Yes" : "No",
      o.consent_text || "",
      o.ip || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `askredline-sms-optins-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // iter195r — bulk archive / unarchive / hard-delete. The endpoint dispatch mirrors
  // the scope selector: acting on "all" hits /admin/optins/*; a single-community view
  // hits the tenant-scoped variant so managers can only ever act inside their tenant.
  const bulkArchive = () => runBulk("archive");
  const bulkUnarchive = () => runBulk("unarchive");
  const bulkDelete = () => {
    if (!window.confirm(`Delete ${selected.size} consent record${selected.size === 1 ? "" : "s"}? This is permanent — the record leaves the DB entirely. Only do this for duplicates or test data.`)) return;
    runBulk("delete");
  };
  const runBulk = async (kind) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const url = scope === "all" ? `/admin/optins/${kind}` : `/tenants/${scope}/optins/${kind}`;
      const r = await api.post(url, { ids });
      const n = r.data.archived || r.data.unarchived || r.data.deleted || 0;
      toast.success(`${n} record${n === 1 ? "" : "s"} ${kind === "delete" ? "deleted" : kind === "unarchive" ? "restored" : "archived"}`);
      setSelected(new Set());
      loadOptins();
    } catch (e) { toast.error(e.response?.data?.detail || `Bulk ${kind} failed`); }
    finally { setBusy(false); }
  };
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    if (prev.size === filtered.length) return new Set();
    return new Set(filtered.map((o) => o.id));
  });

  return (
    <div className="space-y-6" data-testid="optins-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageTitle title={<><ShieldCheck className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> SMS Opt-Ins &amp; Opt-Outs</>} about={<>Verifiable consent records for A2P 10DLC & TCPA compliance.{" "}
            <span className="font-semibold text-ink">{scoped.length}{scope !== "all" ? ` in this ${V.lc("org")}` : " total"}</span></>} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="optins-community-select" value={scope} onChange={(e) => setScope(e.target.value)}
            className="rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-redline-300 shadow-sm">
            <option value="all">All {V.lcs("org")}</option>
            {(tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label data-testid="optins-archived-toggle" className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium cursor-pointer hover:border-sand-300 transition-colors">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-redline-500" />
            Show archived
          </label>
          <button data-testid="optins-export-btn" onClick={exportCsv} disabled={scoped.length === 0}
            className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors disabled:opacity-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* iter195r — bulk action bar. Only appears when the manager has selected rows. */}
      {selected.size > 0 && (
        <div data-testid="optins-bulk-bar" className="sticky top-2 z-10 flex flex-wrap items-center gap-2 rounded-2xl border border-redline-200 bg-white px-4 py-3 shadow-md">
          <span className="font-heading font-bold text-ink">{selected.size} selected</span>
          {!showArchived ? (
            <button data-testid="optins-bulk-archive" onClick={bulkArchive} disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-ink text-white font-semibold px-4 py-2 text-sm hover:bg-ink-700 transition-colors disabled:opacity-50">
              <Archive className="h-3.5 w-3.5" /> Archive
            </button>
          ) : (
            <button data-testid="optins-bulk-unarchive" onClick={bulkUnarchive} disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white text-ink font-semibold px-4 py-2 text-sm hover:border-ink transition-colors disabled:opacity-50">
              <ArchiveRestore className="h-3.5 w-3.5" /> Restore
            </button>
          )}
          <button data-testid="optins-bulk-delete" onClick={bulkDelete} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 text-red-700 font-semibold px-4 py-2 text-sm hover:bg-red-100 transition-colors disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
          <button data-testid="optins-bulk-clear" onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-ink">
            <ClearIcon className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-sand-200 bg-ink text-white p-4" data-testid="optins-stat-total">
          <div className="text-[11px] uppercase tracking-wide font-bold text-sand-300">Total consents</div>
          <div className="font-heading text-3xl font-extrabold mt-1">{scoped.length}</div>
        </div>
        {analytics.bySource.map((s) => (
          <div key={s.key} className="rounded-2xl border border-sand-200 bg-white p-4" data-testid={`optins-stat-${s.key}`}>
            <div className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground flex items-center gap-1.5">
              <s.icon className="h-3.5 w-3.5 text-redline-500" /> {s.label}
            </div>
            <div className="font-heading text-3xl font-extrabold mt-1">{s.count}</div>
          </div>
        ))}
      </div>

      {scope === "all" && (
      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="optins-by-community">
        <div className="px-5 py-3 border-b border-sand-200 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-redline-500" strokeWidth={1.75} />
          <span className="font-heading font-bold">Consents by {V.lc("org")}</span>
        </div>
        {analytics.byCommunity.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No consents recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-bold">{V.one("org")}</th>
                  <th className="px-4 py-3 font-bold text-center">Web / QR</th>
                  <th className="px-4 py-3 font-bold text-center">Text-in</th>
                  <th className="px-4 py-3 font-bold text-center">Roster</th>
                  <th className="px-4 py-3 font-bold text-center">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-100">
                {analytics.byCommunity.map((c) => (
                  <tr key={c.community} data-testid={`optins-community-row`} className="hover:bg-sand-50 transition-colors">
                    <td className="px-4 py-3 font-medium">{c.community}</td>
                    <td className="px-4 py-3 text-center tabular-nums">{c.web_opt_in_page || "—"}</td>
                    <td className="px-4 py-3 text-center tabular-nums">{c.inbound_text || "—"}</td>
                    <td className="px-4 py-3 text-center tabular-nums">{c.roster || "—"}</td>
                    <td className="px-4 py-3 text-center font-bold tabular-nums">{c.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      <ConsentRepairCard scope={scope} tenants={tenants} onRepaired={loadOptouts} />

      {/* STOP list — the flip side of consent. Same page so admins manage both together. */}
      {(scope === "all" ? optouts : optouts.filter((o) => o.tenant_id === scope)).length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden" data-testid="admin-optouts-section">
          <div className="px-5 py-3 border-b border-amber-100 bg-amber-50/50 flex items-center gap-2">
            <BellOff className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
            <span className="font-heading font-bold">Opt-outs (texted STOP)</span>
            <span className="rounded-full bg-amber-500 text-white text-[11px] font-bold px-2 py-0.5">
              {(scope === "all" ? optouts : optouts.filter((o) => o.tenant_id === scope)).length}
            </span>
            <span className="ml-auto text-[11px] text-muted-foreground hidden sm:block">These numbers receive no texts. Restore only if the {V.lc("person")} asked; texting START always works too.</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-bold">{V.one("person")}</th>
                  <th className="px-4 py-3 font-bold">Phone</th>
                  <th className="px-4 py-3 font-bold">{V.one("org")}</th>
                  <th className="px-4 py-3 font-bold">Opted out</th>
                  <th className="px-4 py-3 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-100">
                {(scope === "all" ? optouts : optouts.filter((o) => o.tenant_id === scope)).map((o) => (
                  <tr key={o.person_id} data-testid={`admin-optout-row-${o.person_id}`} className="hover:bg-sand-50 transition-colors">
                    <td className="px-4 py-3 font-medium">{o.name || "—"}{homeLabel(o) && <span className="text-muted-foreground font-normal"> · {homeLabel(o)}</span>}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-amber-500" /> {o.phone}</span></td>
                    <td className="px-4 py-3">{o.community || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{o.opted_out_at ? new Date(o.opted_out_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`admin-optout-restore-${o.person_id}`}
                        onClick={async () => {
                          try {
                            await api.post(`/tenants/${o.tenant_id}/optouts/${o.person_id}/restore`);
                            toast.success("Texting restored", { description: "If their carrier still blocks messages, they should text START." });
                            loadOptouts();
                          } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-300 text-amber-900 text-xs font-semibold px-3 py-1.5 hover:border-amber-500 transition-colors">
                        <Undo2 className="h-3.5 w-3.5" /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-sand-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input data-testid="optins-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search name, phone or ${V.lc("org")}`}
          className="w-full rounded-full border border-sand-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-redline-500/40 focus:border-redline-400" />
      </div>

      {scoped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center">
          <ShieldCheck className="h-10 w-10 text-sand-300 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading font-bold">No opt-in records{scope !== "all" ? ` for this ${V.lc("org")}` : " yet"}</div>
          <p className="text-sm text-muted-foreground mt-1">Consents captured at /opt-in will appear here as auditable proof.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-3 w-8">
                    <input data-testid="optins-select-all" type="checkbox"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleAll}
                      className="accent-redline-500" />
                  </th>
                  <th className="px-4 py-3 font-bold">{V.one("person")}</th>
                  <th className="px-4 py-3 font-bold">Phone</th>
                  <th className="px-4 py-3 font-bold">{V.one("org")}</th>
                  <th className="px-4 py-3 font-bold">Source</th>
                  <th className="px-4 py-3 font-bold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-100">
                {filtered.map((o) => {
                  const Icon = SOURCE_ICON[o.source] || Globe;
                  const isSel = selected.has(o.id);
                  return (
                    <tr key={o.id} data-testid={`optin-row-${o.id}`}
                        className={`hover:bg-sand-50 transition-colors ${isSel ? "bg-redline-50/60" : ""} ${o.archived ? "opacity-60" : ""}`}>
                      <td className="px-3 py-3">
                        <input data-testid={`optin-select-${o.id}`} type="checkbox"
                          checked={isSel} onChange={() => toggle(o.id)}
                          className="accent-redline-500" />
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {o.name || <span className="text-muted-foreground">—</span>}
                        {o.archived && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-sand-200 text-ink-600 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5"><Archive className="h-2.5 w-2.5" /> Archived</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-redline-500" /> {o.phone}</span>
                      </td>
                      <td className="px-4 py-3">
                        {o.community
                          ? <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-sand-400" /> {o.community}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-sand-100 px-2.5 py-1 text-xs font-medium">
                          <Icon className="h-3.5 w-3.5" /> {SOURCE_LABEL[o.source] || o.source}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// Consent Repair — reconcile the roster against the authoritative STOP list (db.optouts),
// per community. Tighten-only: it can only ADD suppression, never opt anyone back in.
function ConsentRepairCard({ scope, tenants, onRepaired }) {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const communityName = (tenants || []).find((t) => t.id === scope)?.name || `this ${V.lc("org")}`;
  const isAll = scope === "all" || !scope;

  // A community change invalidates any previous scan.
  useEffect(() => { setScan(null); }, [scope]);

  const runScan = async () => {
    if (isAll) return;
    setScanning(true);
    try {
      const r = await api.get(`/tenants/${scope}/consent/repair-scan`);
      setScan(r.data);
      if (r.data.clean) toast.success("Consent matches the STOP list — nothing to fix.");
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const runRepair = async () => {
    if (isAll || !scan || scan.clean) return;
    const c = scan.counts || {};
    if (!window.confirm(
      `Repair consent for ${communityName}?\n\n` +
      `• ${c.under_suppressed || 0} ${V.lc("person")}(s) who replied STOP will be set to opted-out\n` +
      `• ${c.missing_from_stoplist || 0} opted-out ${V.lc("person")}(s) will be added to the STOP list\n\n` +
      `This only ADDS suppression — nobody is opted back in. This is logged.`)) return;
    setRepairing(true);
    try {
      const r = await api.post(`/tenants/${scope}/consent/repair`);
      toast.success(`Fixed ${r.data.total_fixed} record${r.data.total_fixed === 1 ? "" : "s"}`, {
        description: `${r.data.fixed_under} set opted-out · ${r.data.backfilled} added to the STOP list`,
      });
      await runScan();
      onRepaired && onRepaired();
    } catch (e) { toast.error(e.response?.data?.detail || "Repair failed"); }
    finally { setRepairing(false); }
  };

  const counts = scan?.counts || {};
  const hasDrift = scan && !scan.clean;

  return (
    <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="consent-repair-card">
      <div className="px-5 py-3 border-b border-sand-200 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-redline-500" strokeWidth={1.75} />
        <span className="font-heading font-bold">Consent integrity check</span>
        <span className="ml-auto text-[11px] text-muted-foreground hidden sm:block">Reconciles the roster with the STOP list. Only ever adds suppression — never opts anyone back in.</span>
      </div>
      <div className="p-5">
        {isAll ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="consent-repair-pick-community">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Pick a specific {V.lc("org")} above to run a consent check.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="consent-scan-btn" onClick={runScan} disabled={scanning || repairing}
                className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors disabled:opacity-50">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {scanning ? "Scanning…" : "Run consent check"}
              </button>
              <span className="text-sm text-muted-foreground">for <span className="font-semibold text-ink">{communityName}</span></span>
            </div>

            {scan && scan.clean && (
              <div data-testid="consent-clean" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Consent matches the STOP list. Nothing to fix.
              </div>
            )}

            {hasDrift && (
              <div data-testid="consent-scan-result" className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="consent-under-block">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-bold text-red-700">
                      <ShieldAlert className="h-3.5 w-3.5" /> Under-suppressed
                    </div>
                    <div className="font-heading text-3xl font-extrabold mt-1 text-red-800" data-testid="consent-under-count">{counts.under_suppressed || 0}</div>
                    <p className="text-xs text-red-700/80 mt-1">Said STOP but the roster shows them opted-in. They could slip through an overlooked send path.</p>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="consent-missing-block">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-bold text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5" /> Not on the STOP list
                    </div>
                    <div className="font-heading text-3xl font-extrabold mt-1 text-amber-800" data-testid="consent-missing-count">{counts.missing_from_stoplist || 0}</div>
                    <p className="text-xs text-amber-700/80 mt-1">Opted-out on the roster but missing from the authoritative do-not-text list.</p>
                  </div>
                </div>

                {(scan.under_suppressed || []).length > 0 && (
                  <div className="overflow-x-auto rounded-xl border border-sand-200">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-sand-50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-4 py-2 font-bold">{V.one("person")}</th><th className="px-4 py-2 font-bold">Phone</th><th className="px-4 py-2 font-bold">Roster status</th>
                      </tr></thead>
                      <tbody className="divide-y divide-sand-100">
                        {(scan.under_suppressed || []).slice(0, 25).map((p) => (
                          <tr key={p.person_id} data-testid={`consent-under-row-${p.person_id}`}>
                            <td className="px-4 py-2 font-medium">{p.name || "—"}{homeLabel(p) && <span className="text-muted-foreground font-normal"> · {homeLabel(p)}</span>}</td>
                            <td className="px-4 py-2"><span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-red-500" /> {p.phone}</span></td>
                            <td className="px-4 py-2 text-muted-foreground">{p.consent_status || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button data-testid="consent-repair-btn" onClick={runRepair} disabled={repairing || scanning}
                  className="inline-flex items-center gap-2 rounded-full bg-redline-600 text-white font-semibold px-5 py-2.5 text-sm hover:bg-redline-700 transition-colors disabled:opacity-50">
                  {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  {repairing ? "Repairing…" : `Repair ${counts.total || 0} record${(counts.total || 0) === 1 ? "" : "s"}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
